import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-relay';

import { SettingTypeInput } from '../graphql/__generated__/UpdateSettingMutation.graphql';
import UpdateSettingMutation from '../graphql/mutations/UpdateSettingMutation';
import { SettingGantDataType, UserProjectSettingsType, UserSettingsType } from '../types';
import useCommitSetting from './useCommitSetting';
import { useSettingsOnce } from './useSettingsOnce';

type DeepPartial<T> = T extends Function
    ? T
    : T extends Array<infer U>
      ? Array<DeepPartial<U>>
      : T extends object
        ? { [K in keyof T]?: DeepPartial<T[K]> }
        : T;

export const useSettings = () => {
    const { settingGant, connectionSettingGant, authorId } = useSettingsOnce();
    const [commitUpdate] = useMutation(UpdateSettingMutation);
    const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);
    const { getCommitSetting } = useCommitSetting();

    const saveUserSettings = useCallback(
        (settings: DeepPartial<UserSettingsType>) => {
            if (!settingGant?.id) {
                return;
            }
            const newSettings = {
                userSettings: {
                    [authorId]: settings,
                },
            } as SettingGantDataType;

            const newSettingGantForServer = {
                data: JSON.stringify(newSettings),
            } as SettingTypeInput;

            commitUpdate(
                getCommitSetting(
                    newSettingGantForServer,
                    connectionSettingGant,
                    settingGant?.id,
                    () => {
                        console.log('settings save');
                    },
                    true,
                ),
            );
        },
        [settingGant, connectionSettingGant, authorId],
    );

    const saveUserProjectSettings = useCallback(
        (projectId: string, settings: Partial<UserProjectSettingsType>) => {
            saveUserSettings({
                project: {
                    [projectId]: settings,
                },
            });
        },
        [saveUserSettings],
    );

    const userSettingsInitial = useMemo(
        () => (settingGant?.data?.userSettings?.[authorId] ?? {}) as UserSettingsType,
        [settingGant?.data?.userSettings?.[authorId]],
    );

    const userSettingsInitialRef = useRef(userSettingsInitial);

    useEffect(() => {
        userSettingsInitialRef.current = JSON.parse(JSON.stringify(userSettingsInitial));
        // TODO: this is bad. Needed only to ensure that settings were pushed to the redux store.
        setTimeout(() => {
            setIsSettingsLoaded(true);
        }, 10);
    }, [userSettingsInitial]);

    return {
        saveUserSettings,
        saveUserProjectSettings,
        userSettingsInitialRef,
        isSettingsLoaded,
    };
};
